// Scheduled training sessions — data access.
//
// Sessions are authored in the admin portal and served by web-api
// (`GET /training`). This module maps that payload for the public Training page
// and exposes a react-query hook, mirroring content/bulletin.ts.

import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TrainingSessionSummary, TrainingTopicSummary } from '@munifocus/contracts-web';
import { api } from '../lib/api-client';
import { frenchFirstOfMonth } from '@munifocus/utils';

function currentLang(language: string | undefined): 'en' | 'fr' {
  return language?.startsWith('fr') ? 'fr' : 'en';
}

export function useTrainingSessions() {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  return useQuery({
    queryKey: ['training', lang],
    queryFn: async () => {
      const { data } = await api.get<TrainingSessionSummary[]>('/training', { params: { lang } });
      return data;
    },
  });
}

const SESSION_LOCALES = ['en', 'fr'] as const;

/**
 * Scheduled sessions across BOTH languages, merged and de-duplicated by slug.
 * Sessions are published per-locale (a session appears in the `en` payload only
 * if it's available in English, etc.), so the public page can show English and
 * French courses together and let visitors filter by course language. A session
 * available in both locales is shown once, rendered in the current UI language,
 * with `availableLocales` carrying both.
 */
export function useTrainingSessionsAllLocales() {
  const { i18n } = useTranslation();
  const uiLang = currentLang(i18n.language);
  const results = useQueries({
    queries: SESSION_LOCALES.map((lang) => ({
      queryKey: ['training', lang],
      queryFn: async () => {
        const { data } = await api.get<TrainingSessionSummary[]>('/training', { params: { lang } });
        return data;
      },
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const lists = results.map((r) => r.data ?? []);
  const data = useMemo(() => mergeSessionsBySlug(lists, uiLang), [JSON.stringify(lists), uiLang]);
  return { data, isLoading };
}

function mergeSessionsBySlug(
  lists: TrainingSessionSummary[][],
  uiLang: 'en' | 'fr',
): TrainingSessionSummary[] {
  const map = new Map<string, TrainingSessionSummary>();
  for (const list of lists) {
    for (const s of list) {
      const existing = map.get(s.slug);
      const locales = new Set([...(s.availableLocales ?? [s.language])]);
      if (!existing) {
        map.set(s.slug, { ...s, availableLocales: [...locales] });
        continue;
      }
      for (const l of existing.availableLocales ?? []) locales.add(l);
      // For a bilingual session, render it in the current UI language.
      const base = s.language === uiLang ? s : existing;
      map.set(s.slug, { ...base, availableLocales: [...locales] });
    }
  }
  return [...map.values()].sort(
    (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
  );
}

/** Flagship training topics (the curriculum catalogue), authored in the admin
 * portal and published to the public site. Ordered by curriculum display order. */
export function useTrainingTopics() {
  const { i18n } = useTranslation();
  const lang = currentLang(i18n.language);
  return useQuery({
    queryKey: ['training-topics', lang],
    queryFn: async () => {
      const { data } = await api.get<TrainingTopicSummary[]>('/training-topics', {
        params: { lang },
      });
      return data;
    },
  });
}

export function formatSessionDate(iso: string, lang: 'en' | 'fr'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return frenchFirstOfMonth(
    d.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
  );
}

/**
 * Whether the generic "Enroll in a session" flow offers this session: it has
 * not started on an earlier day and is not a private booking (whose details are
 * withheld). Closed sessions are included — an officer can point people at the
 * enroll form for a session whose public calendar enrollment is closed.
 */
export function isEnrollable(s: TrainingSessionSummary, now: Date = new Date()): boolean {
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return new Date(s.startsAt) >= todayStart && (s.enrollment ?? 'open') !== 'private';
}

/**
 * Whether a session can still be registered for: it has not started on an
 * earlier day, and its registration is open (not a closed or private booking).
 * The rule the training page applies, for anywhere else that offers sessions.
 */
export function isOpenForRegistration(s: TrainingSessionSummary, now: Date = new Date()): boolean {
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return new Date(s.startsAt) >= todayStart && (s.enrollment ?? 'open') === 'open';
}
